import axios from "axios"
import { IOrgAttachment, TPdfLayoutType } from "@services/pdf/types"

/**
 * Org attachment pages for the PDF builder ("{artist} info" PDFs).
 * iOS fetches these directly from Airtable (OrganisationWebService +
 * OrganisationStore) for White Cube layout orgs only. Interim approach agreed
 * with the team: same Airtable call behind an env token, degrading to no
 * attachments when the token is absent; a Go-backend proxy endpoint will
 * replace this later.
 */

const airtableBaseId = "appCW0Gj148XWgDoS" // iOS OrganisationStore hardcodes this for wc orgs

/**
 * Attachment/thumbnail URLs are fetched and (for PDFs) rendered by the app, so
 * only accept Airtable's own attachment hosts — a compromised record must not
 * be able to point us at an arbitrary origin.
 */
function isAllowedAttachmentUrl(raw: string): boolean {
	try {
		const url = new URL(raw)
		return (
			url.protocol === "https:" &&
			(url.hostname === "dl.airtable.com" || url.hostname.endsWith(".airtableusercontent.com"))
		)
	} catch {
		return false
	}
}

interface IAirtableThumbnailContent {
	url?: string
}

interface IAirtableAttachment {
	id?: string
	url?: string
	filename?: string
	type?: string
	thumbnails?: { small?: IAirtableThumbnailContent; large?: IAirtableThumbnailContent }
}

interface IAirtableRecord {
	id?: string
	fields?: {
		Artist?: string
		Attachments?: IAirtableAttachment[]
	}
}

export async function fetchOrgAttachments(layoutType: TPdfLayoutType): Promise<IOrgAttachment[]> {
	const token = process.env.REACT_APP_AIRTABLE_KEY
	// iOS only queries Airtable for wc-layout orgs
	if (layoutType !== "wc" || !token) {
		return []
	}

	try {
		const attachments: IOrgAttachment[] = []
		// Airtable caps list responses at 100 records and returns an `offset`
		// cursor to page through the rest — follow it until exhausted.
		let offset: string | undefined
		// safety cap: 100 pages = 10k records, far beyond any real Artists table
		for (let page = 0; page < 100; page++) {
			const { data } = await axios.get<{ records?: IAirtableRecord[]; offset?: string }>(
				`https://api.airtable.com/v0/${airtableBaseId}/Artists`,
				{ headers: { Authorization: `Bearer ${token}` }, params: offset ? { offset } : undefined }
			)

			;(data.records ?? []).forEach((record, recordIndex) => {
				const artist = record.fields?.Artist ?? ""
				;(record.fields?.Attachments ?? []).forEach((attachment, attachmentIndex) => {
					// iOS keeps PDF attachments only
					const pdfPath = attachment.url ?? ""
					if (!(attachment.type ?? "").endsWith("pdf") || !isAllowedAttachmentUrl(pdfPath)) {
						return
					}
					const thumbnailUrl = attachment.thumbnails?.large?.url ?? ""
					attachments.push({
						id: attachment.id ?? `${page}-${recordIndex}-${attachmentIndex}`,
						artist,
						pdfName: attachment.filename ?? "",
						pdfPath,
						pdfImagePath: isAllowedAttachmentUrl(thumbnailUrl) ? thumbnailUrl : "",
					})
				})
			})

			offset = data.offset
			if (!offset) {
				break
			}
		}
		return attachments
	} catch {
		return []
	}
}
